import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { API } from "@/lib/api";
import { PLACES } from "@/lib/bibleMap";

type Pos = (x: number, y: number, dx?: number, dy?: number) => { left: string; top: string };

// Points on open water (Mediterranean, Red Sea, Galilee, Persian Gulf) in map %
const GLINTS: [number, number][] = [[8, 30], [16, 44], [24, 34], [30, 48], [12, 56], [36, 40], [20, 24], [6, 46], [37, 69], [35.5, 64], [47.3, 44.5], [94, 74], [28, 40]];

export function Glints({ pos }: { pos: Pos }) {
  return <>{GLINTS.map(([x, y], i) => (
    <span key={i} className="ml-glint pointer-events-none absolute z-[4]" style={{ ...pos(x, y), animationDelay: `${(i * 0.73) % 4}s` }} />
  ))}</>;
}

export function Clouds() {
  return (
    <div className="pointer-events-none absolute inset-0 z-[6] overflow-hidden" data-testid="map-clouds">
      {[{ t: "8%", d: 70, s: 1, dl: -10 }, { t: "38%", d: 95, s: 1.4, dl: -55 }, { t: "64%", d: 80, s: 0.9, dl: -30 }].map((c, i) => (
        <div key={i} className="ml-cloud absolute left-0" style={{ top: c.t, animationDuration: `${c.d}s`, animationDelay: `${c.dl}s`, transform: `scale(${c.s})` }}>
          <div className="ml-cloud-shadow" /><div className="ml-cloud-puff" />
        </div>
      ))}
    </div>
  );
}

export function Birds() {
  return (
    <div className="pointer-events-none absolute inset-0 z-[25] overflow-hidden" data-testid="map-birds">
      {[{ t: "18%", d: 22, dl: 2 }, { t: "30%", d: 28, dl: 13 }].map((b, i) => (
        <div key={i} className="ml-flock absolute left-0" style={{ top: b.t, animationDuration: `${b.d}s`, animationDelay: `${b.dl}s` }}>
          {[[0, 0], [18, 8], [-14, 10], [30, -4]].map(([x, y], j) => (
            <svg key={j} viewBox="0 0 24 10" className="ml-wing absolute h-3 w-6" style={{ left: x, top: y, animationDelay: `${j * 0.15}s` }}>
              <path d="M1 7 Q6 1 12 7 Q18 1 23 7" fill="none" stroke="#3b3a36" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          ))}
        </div>
      ))}
    </div>
  );
}

const SCENES: { id: string; place: string; dx: number; dy: number; el: JSX.Element }[] = [
  { id: "star", place: "bethlehem", dx: -5.5, dy: -8, el: <svg viewBox="0 0 40 40" className="ml-twinkle h-11 w-11"><path d="M20 2 L23 17 L38 20 L23 23 L20 38 L17 23 L2 20 L17 17 Z" fill="#FFE9A8" stroke="#E0A93B" strokeWidth="1.2" /></svg> },
  { id: "waves", place: "redsea", dx: -2.5, dy: 2, el: <svg viewBox="0 0 48 24" className="h-8 w-16">{[0, 1].map((k) => <path key={k} className="ml-wave" style={{ animationDelay: `${k * 0.8}s` }} d={`M2 ${10 + k * 8} Q8 ${4 + k * 8} 14 ${10 + k * 8} T26 ${10 + k * 8} T38 ${10 + k * 8} T48 ${10 + k * 8}`} fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" />)}</svg> },
  { id: "fire", place: "carmel", dx: -5.5, dy: 1, el: <svg viewBox="0 0 30 40" className="ml-flicker h-11 w-8"><path d="M15 2 C22 12 27 18 25 28 C23 36 7 36 5 28 C3 20 10 16 15 2 Z" fill="#F08A3C" /><path d="M15 14 C19 20 21 24 19 30 C17 34 13 34 11 30 C9 25 13 22 15 14 Z" fill="#FFD36A" /></svg> },
  { id: "rainbow", place: "ararat", dx: 4, dy: 3, el: <svg viewBox="0 0 60 32" className="ml-glow h-10 w-20">{["#E8806B", "#F2C46D", "#9CC79A", "#7FB3C8"].map((c, k) => <path key={c} d={`M${6 + k * 4} 30 A${24 - k * 4} ${24 - k * 4} 0 0 1 ${54 - k * 4} 30`} fill="none" stroke={c} strokeWidth="3" opacity=".85" />)}</svg> },
  { id: "fish", place: "galilee", dx: 5, dy: -2, el: <svg viewBox="0 0 30 20" className="ml-jump h-7 w-9"><path d="M4 10 Q12 2 22 10 Q12 18 4 10 Z M22 10 L28 5 L28 15 Z" fill="#9FC3D4" stroke="#4E7A8C" strokeWidth="1" /></svg> },
];

export function Scenes({ pos, avoid = [] }: { pos: Pos; avoid?: { x: number; y: number }[] }) {
  return <>{SCENES.map((s) => {
    const p = PLACES[s.place];
    const at = pos(p[0], p[1], s.dx, s.dy), x = parseFloat(at.left), y = parseFloat(at.top);
    if (avoid.some((a) => Math.hypot(a.x - x, (a.y - y) * 0.67) < 4.5)) return null;
    return (
      <div key={s.id} data-testid={`map-scene-${s.id}`} className="pointer-events-none absolute z-[5] -translate-x-1/2 -translate-y-1/2 drop-shadow" style={pos(p[0], p[1], s.dx, s.dy)}>{s.el}</div>
    );
  })}</>;
}

// One shared soundscape for all maps on the page
const shared: { tracks: HTMLAudioElement[]; users: number } = { tracks: [], users: 0 };
const startShared = () => shared.tracks.forEach((a) => a.play().catch(() => {}));

export function MapSound() {
  const [on, setOn] = useState(() => localStorage.getItem("cast_map_sound") !== "off");
  useEffect(() => {
    const sync = (e: Event) => setOn((e as CustomEvent).detail);
    window.addEventListener("cast-map-sound", sync);
    return () => window.removeEventListener("cast-map-sound", sync);
  }, []);
  useEffect(() => {
    if (!shared.users++) shared.tracks = [["sea", 0.14], ["garden", 0.05]].map(([k, v]) => { const a = new Audio(`${API}/media/ambience/${k}`); a.loop = true; a.volume = +v; return a; });
    return () => { if (!--shared.users) { shared.tracks.forEach((a) => a.pause()); shared.tracks = []; } };
  }, []);
  useEffect(() => {
    if (!on) { shared.tracks.forEach((a) => a.pause()); return; }
    startShared();
    const kick = () => startShared();
    document.addEventListener("pointerdown", kick, { once: true });
    return () => document.removeEventListener("pointerdown", kick);
  }, [on]);
  const toggle = () => { const v = !on; localStorage.setItem("cast_map_sound", v ? "on" : "off"); window.dispatchEvent(new CustomEvent("cast-map-sound", { detail: v })); };
  return (
    <button onClick={toggle} data-testid="map-sound-toggle" aria-label={on ? "Mute map sounds" : "Play map sounds"} title={on ? "Mute map sounds" : "Play map sounds"}
      className="absolute right-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-lg ring-1 ring-[#E2DBCE] transition-transform hover:scale-110">
      {on ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
    </button>
  );
}
